<template>
  <v-container fill-height fluid>
    <v-row justify="center">
      <v-col cols="12">
        <base-material-card :title="title">
          <v-form @submit.prevent="handleSubmit">
            <v-container class="py-0">
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    autocomplete="off"
                    data-lpignore="true"
                    :disabled="isReadOnly"
                    v-model="resource.name"
                    name="name"
                    label="Name"
                  />
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    type="number"
                    autocomplete="off"
                    data-lpignore="true"
                    :disabled="isReadOnly"
                    v-model="resource.code"
                    name="code"
                    label="Code"
                  />
                </v-col>

                <v-col cols="6">
                  <v-autocomplete
                    autocomplete="off"
                    class="color-select"
                    :disabled="isReadOnly"
                    :items="colors"
                    name="color"
                    label="Display Color"
                    v-model="resource.color"
                    item-text="name"
                    item-value="id"
                    hide-details
                    :background-color="resource.color"
                  />
                </v-col>

                <v-col cols="3">
                  <v-text-field
                    type="number"
                    :value="ppg"
                    autocomplete="off"
                    data-lpignore="true"
                    :disabled="isReadOnly"
                    @change="ppgChanged"
                    name="ppg"
                    label="PPG"
                    @keydown.native.enter.prevent
                    reverse
                  />
                </v-col>

                <v-col cols="3">
                  <v-text-field
                    type="number"
                    :value="specificGravity"
                    autocomplete="off"
                    data-lpignore="true"
                    :disabled="isReadOnly"
                    @change="specificGravityChanged"
                    name="specific-gravity"
                    label="Specific Gravity"
                    @keydown.native.enter.prevent
                    reverse
                  />
                </v-col>

                <v-col cols="6">
                  <v-autocomplete
                    autocomplete="off"
                    :disabled="isReadOnly"
                    :items="chemicalTypes"
                    name="type"
                    label="Type"
                    v-model="resource.chemicalType"
                    item-text="name"
                    item-value="id"
                    hide-details
                  />
                </v-col>

                <v-col cols="6">
                  <v-autocomplete
                    :disabled="autoSelectManufacturer"
                    autocomplete="off"
                    :items="chemicalManufacturers"
                    name="chemicalManufacturer"
                    label="Chemical Manufacturer"
                    v-model="resource.chemicalManufacturer"
                    item-text="name"
                    item-value="id"
                    hide-details
                    return-object
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    autocomplete="off"
                    data-lpignore="true"
                    :disabled="isReadOnly"
                    v-model="resource.description"
                    name="description"
                    label="Description"
                  />
                </v-col>
              </v-row>
              <v-row>
                <FormActions
                  :cancel="handleCancel"
                  :submit="handleSubmit"
                  :destroy="handleDestroy"
                  :canDestroy="canDestroy"
                  :canEdit="canEdit"
                  :canCreate="canCreate"
                  :isValid="isValid"
                  :submitting="submitting"
                />
              </v-row>
            </v-container>
          </v-form>
        </base-material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import _ from 'lodash';

import Formable from '@/mixins/formable';

const WATER_DENSITY = 8.345;

export default {
  mixins: [Formable],
  props: {
    chemicalTypes: {
      type: Array,
      default: () => [],
    },
    chemicalManufacturers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      resourceName: 'Chemical',
      specificGravity: this.ppgAsSG(this.resource.ppg),
      ppg: this.resource.ppg,
      colors: [
        { name: 'Default', id: '#2296c8' },
        { name: 'Pink', id: '#F8F' },
        { name: 'Orange', id: '#FF8533' },
        { name: 'Yellow', id: '#FFDB4D' },
        { name: 'Green', id: '#4DB84D' },
        { name: 'Blue', id: '#4D70DB' },
        { name: 'Purple', id: '#A375FF' },
        { name: 'Brown', id: '#855C33' },
        { name: 'Clear', id: '#E6E6E6' },
      ],
    };
  },
  computed: {
    autoSelectManufacturer() {
      return this.isReadOnly || this.chemicalManufacturers.length === 1;
    },
    isValid() {
      return !!(
        this.resource.name &&
        this.resource.code &&
        this.resource.color &&
        this.resource.chemicalType
      );
    },
  },
  watch: {
    chemicalManufacturers: {
      immediate: true,
      handler(chemicalManufacturers) {
        if (chemicalManufacturers.length === 1) {
          this.resource.chemicalManufacturer = chemicalManufacturers[0];
        }
      },
    },
  },
  methods: {
    ppgAsSG(ppg) {
      return (ppg / WATER_DENSITY).toFixed(4);
    },
    sgAsPPG(sg) {
      return (sg * WATER_DENSITY).toFixed(2);
    },
    specificGravityChanged(value) {
      const newSG = parseFloat(value);
      const oldSG = this.ppgAsSG(_.get(this.resource, 'ppg', 0));
      if (newSG !== oldSG) {
        const ppg = this.sgAsPPG(newSG);
        this.ppg = ppg;
        this.resource.ppg = ppg;
      }
    },
    ppgChanged(value) {
      const newPPG = parseFloat(value);
      const oldPPG = _.get(this.resource, 'ppg', 0);
      if (newPPG !== oldPPG) {
        this.specificGravity = this.ppgAsSG(newPPG);
        this.resource.ppg = newPPG;
      }
    },
  },
};
</script>

<style lang="scss">
.color-select {
  .v-input__slot {
    box-shadow: inset 0 0 1px 2px white;
  }
  .v-select__slot {
    padding-left: 8px;
  }
  &:not(.v-input--is-label-active) .v-input__slot {
    padding-left: 8px;
  }
}
</style>
